import { graphql } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { cn, getPositionClass, getSettingsClass } from '~/utils/helpers';
import { useViewport } from '~/utils/viewport-provider';

export const ViewportImage = ({
  image,
  className = '',
}: {
  image?: ImageField<RegularImage>;
  className?: string;
}) => {
  if (!image) return null;

  const { isMobile } = useViewport();
  const { scale, offset, responsive_width } = image;
  const offsetUnit = 'rem';

  let width: string | undefined = undefined;
  if (responsive_width) {
    width = `${isMobile ? responsive_width.mobile : responsive_width.desktop}${responsive_width.unit}`;
  }

  let marginTop: string | undefined = undefined;
  let marginBottom: string | undefined = undefined;
  if (offset?.vertical) {
    // if offset.vertical exists, we need to set marginTop or marginBottom
    if (offset.vertical > 0) {
      // we need to add negative marginTop if > 0
      marginTop = `-${offset.vertical}${offsetUnit}`;
    } else if (offset.vertical < 0) {
      // we need to set marginBottom if < 0, to respect spacing between elements
      marginBottom = `${offset.vertical}${offsetUnit}`;
    }
  }

  return (
    <img
      src={image.src}
      alt={image.alt}
      className={className}
      style={{
        translate: `${offset?.horizontal ?? 0}rem ${offset?.vertical ?? 0}rem`,
        scale: `${scale?.x ?? 1} ${scale?.y ?? 1}`,
        width,
        marginTop,
        marginBottom,
      }}
    />
  );
};

type BlockViewportImageProps = {
  settings: Settings;
  viewport_image: ImageField<RegularImage>[];
  viewport_position: ViewportPosition;
};

export const BlockViewportImage = ({
  block: { settings, viewport_image: image_template, viewport_position },
}: {
  block: BlockViewportImageProps;
}) => {
  const { isMobile } = useViewport();
  const settingsClass = getSettingsClass(settings);
  const [viewport_image] = image_template ? image_template : [];
  const [positionClass, setPositionClass] = useState('justify-center');

  useEffect(() => {
    if (viewport_position) {
      if (isMobile) {
        setPositionClass(getPositionClass(viewport_position.mobile));
      } else {
        setPositionClass(getPositionClass(viewport_position.desktop));
      }
    }
  }, [isMobile]);

  return (
    <div className={cn(`flex w-full ${settingsClass} ${positionClass}`)}>
      <ViewportImage
        image={viewport_image}
        className='w-full'
      />
    </div>
  );
};

export const viewportImageFragment = graphql`
  fragment ViewportImageFragment on Block {
    settings {
      ...SettingsFragment
    }
    viewport_image {
      ...ImageFieldFragment
    }
    viewport_position {
      desktop
      mobile
    }
  }
`;
